import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, Stack, Chip } from '@mui/material';
import moment from 'moment';

interface HeatMapData {
    date: string;
    pnl: number;
    count: number;
}

interface PnLHeatMapProps {
    data: HeatMapData[];
    viewMode: 'paper' | 'live';
    selectedDate?: string;
    onDateClick?: (date: string) => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PnLHeatMap: React.FC<PnLHeatMapProps> = ({ data, viewMode, selectedDate, onDateClick }) => {
    const days = 365;
    const today = moment().startOf('day');
    const startDate = moment().subtract(days, 'days');

    const dataMap = useMemo(() => {
        const map = new Map<string, HeatMapData>();
        if (Array.isArray(data)) {
            data.forEach(d => map.set(d.date, d));
        }
        return map;
    }, [data]);

    // Get color based on P&L - green for profit, red for loss (light mode colors)
    const getColor = (pnl: number, hasData: boolean, isSelected: boolean) => {
        if (isSelected) return '#0969da'; // Highlight selected date
        if (!hasData) return '#ebedf0'; // No trades - light gray
        if (pnl === 0) return '#d0d7de'; // Breakeven
        
        // Losses - Red shades (light mode)
        if (pnl < 0) {
            if (pnl > -500) return '#ffcdd2';
            if (pnl > -2000) return '#ef9a9a';
            if (pnl > -5000) return '#e57373';
            return '#ef5350'; // Bright red for big losses
        }
        
        // Profits - Green shades (GitHub light mode style)
        if (pnl < 500) return '#9be9a8';
        if (pnl < 2000) return '#40c463';
        if (pnl < 5000) return '#30a14e';
        return '#216e39'; // Deep green for big profits
    };

    // Build calendar data with weeks as columns
    const { calendar, monthLabels } = useMemo(() => {
        const weeks: any[][] = [];
        let currentWeek: any[] = [];
        const iter = moment(startDate);
        const monthPositions: { month: string; colSpan: number; startCol: number }[] = [];
        
        // Fill leading empty days if start date isn't Sunday
        const leadingDays = iter.day();
        for (let i = 0; i < leadingDays; i++) {
            currentWeek.push(null);
        }

        let currentMonth = iter.format('MMM');
        let monthStartCol = 0;
        let monthWeekCount = 0;

        while (iter.isSameOrBefore(today)) {
            const dateStr = iter.format('YYYY-MM-DD');
            const dayData = dataMap.get(dateStr);
            
            // Track month changes for header labels
            const iterMonth = iter.format('MMM');
            if (iterMonth !== currentMonth) {
                if (monthWeekCount > 0) {
                    monthPositions.push({ 
                        month: currentMonth, 
                        colSpan: monthWeekCount, 
                        startCol: monthStartCol 
                    });
                }
                currentMonth = iterMonth;
                monthStartCol = weeks.length;
                monthWeekCount = 0;
            }

            currentWeek.push({
                date: dateStr,
                displayDate: iter.format('MMM D, YYYY'),
                pnl: dayData?.pnl || 0,
                count: dayData?.count || 0,
                hasData: !!dayData,
                isToday: iter.isSame(today, 'day'),
                dayOfWeek: iter.day(),
                isSelected: selectedDate === dateStr
            });

            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
                monthWeekCount++;
            }
            iter.add(1, 'day');
        }

        // Push remaining week
        if (currentWeek.length > 0) {
            weeks.push(currentWeek);
            monthWeekCount++;
        }

        // Push final month
        if (monthWeekCount > 0) {
            monthPositions.push({ 
                month: currentMonth, 
                colSpan: monthWeekCount, 
                startCol: monthStartCol 
            });
        }

        return { calendar: weeks, monthLabels: monthPositions };
    }, [startDate, today, dataMap, selectedDate]);

    return (
        <Box sx={{ 
            p: 2, 
            bgcolor: '#ffffff', 
            borderRadius: 2, 
            border: '1px solid #d0d7de', 
            overflowX: 'auto' 
        }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Execution Heat Map (Last 365 Days)</Typography>
                                    <Chip 
                                        label={viewMode === 'paper' ? 'Paper' : 'Live'}
                                        size="small"
                                        color={viewMode === 'paper' ? 'warning' : 'success'}
                                        variant="outlined"
                                    />
                                    {selectedDate && (
                                        <Chip 
                                            label={`Selected: ${moment(selectedDate).format('MMM D, YYYY')}`}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                            onDelete={() => onDateClick?.(moment().format('YYYY-MM-DD'))}
                                        />
                                    )}
                                </Stack>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#24292f' }}>
                
            </Typography>
            
            <Box sx={{ display: 'inline-block', minWidth: 'max-content' }}>
                {/* Month Labels Header */}
                <Box sx={{ display: 'flex', mb: 0.5, ml: '32px' }}>
                    {monthLabels.map((m, i) => (
                        <Box 
                            key={i} 
                            sx={{ 
                                width: m.colSpan * 13, // 10px cell + 3px gap
                                color: '#57606a',
                                fontSize: '10px',
                                textAlign: 'left',
                                pl: 0.5
                            }}
                        >
                            {m.colSpan >= 2 && m.month}
                        </Box>
                    ))}
                </Box>

                {/* Calendar Grid with Day Labels */}
                <Box sx={{ display: 'flex' }}>
                    {/* Day of Week Labels */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5, gap: '3px' }}>
                        {DAYS.map((day, i) => (
                            <Box 
                                key={i}
                                sx={{ 
                                    width: 28,
                                    height: 10,
                                    fontSize: '9px',
                                    color: '#57606a',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    pr: 0.5
                                }}
                            >
                                {/* Show Mon, Wed, Fri labels only (GitHub style) */}
                                {(i === 1 || i === 3 || i === 5) && day}
                            </Box>
                        ))}
                    </Box>

                    {/* Weeks Grid */}
                    <Stack direction="row" spacing="3px">
                        {calendar.map((week, wIndex) => (
                            <Stack key={wIndex} spacing="3px">
                                {week.map((day: any, dIndex: number) => (
                                    <Tooltip 
                                        key={dIndex}
                                        title={day 
                                            ? `${day.displayDate}: ${day.hasData 
                                                ? `Net PnL: ₹${day.pnl >= 0 ? '+' : ''}${day.pnl.toFixed(0)} (${day.count} trade${day.count !== 1 ? 's' : ''})` 
                                                : 'No trades'}`
                                            : ""
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <Box
                                            onClick={() => day && onDateClick?.(day.date)}
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '2px',
                                                bgcolor: day ? getColor(day.pnl, day.hasData, day.isSelected) : 'transparent',
                                                border: day?.isSelected ? '2px solid #000' : day?.isToday ? '2px solid #0969da' : 'none',
                                                cursor: day ? 'pointer' : 'default',
                                                transition: 'all 0.1s ease',
                                                '&:hover': day ? { 
                                                    transform: 'scale(1.2)',
                                                    outline: '1px solid #57606a' 
                                                } : {}
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                {/* Legend */}
                <Stack 
                    direction="row" 
                    spacing={1} 
                    alignItems="center" 
                    sx={{ mt: 2, justifyContent: 'flex-end' }}
                >
                    <Typography variant="caption" sx={{ color: '#57606a' }}>Loss</Typography>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#ef5350', borderRadius: '2px' }} />
                    <Box sx={{ width: 10, height: 10, bgcolor: '#ef9a9a', borderRadius: '2px' }} />
                    <Box sx={{ width: 10, height: 10, bgcolor: '#ebedf0', borderRadius: '2px' }} />
                    <Box sx={{ width: 10, height: 10, bgcolor: '#9be9a8', borderRadius: '2px' }} />
                    <Box sx={{ width: 10, height: 10, bgcolor: '#40c463', borderRadius: '2px' }} />
                    <Box sx={{ width: 10, height: 10, bgcolor: '#216e39', borderRadius: '2px' }} />
                    <Typography variant="caption" sx={{ color: '#57606a' }}>Profit</Typography>
                </Stack>
            </Box>
        </Box>
    );
};

export default PnLHeatMap;
